import React, { useCallback, useMemo, useState } from "react";
import {
  BoothAvailability,
  parseBoothRegion,
  sortBoothNames,
} from "../../Models/BoothRegion";
import { ShowcaseBooth } from "../../Models/ShowcaseBoothMap";
import BoothDetailModal from "./BoothDetailModal";

export interface BoothFloorplanViewerProps {
  floorplanUrl: string;
  booths: ShowcaseBooth[];
  assignments: BoothAvailability[];
  loading?: boolean;
  defaultPanelOpen?: boolean;
  lastRefreshedUtc?: string;
}

const BoothFloorplanViewer: React.FC<BoothFloorplanViewerProps> = ({
  floorplanUrl,
  booths,
  assignments,
  loading = false,
  defaultPanelOpen = true,
  lastRefreshedUtc,
}) => {
  const [modalBooth, setModalBooth] = useState<string | null>(null);

  const assigneeByBooth = useMemo(() => {
    const map = new Map<string, string>();
    assignments.forEach((a) => {
      map.set(a.boothNumber.trim().toLowerCase(), a.companyName);
    });
    return map;
  }, [assignments]);

  const reservedSet = useMemo(
    () => new Set(assigneeByBooth.keys()),
    [assigneeByBooth]
  );

  const allBoothNames = useMemo(
    () => booths.map((b) => b.name).filter(Boolean),
    [booths]
  );

  const getAssignee = useCallback(
    (boothName: string): string => {
      const key = boothName.trim().toLowerCase();
      return assigneeByBooth.get(key) ?? "Unassigned";
    },
    [assigneeByBooth]
  );

  const sortedBoothRoster = useMemo(() => {
    return sortBoothNames(allBoothNames).map((name) => {
      const key = name.trim().toLowerCase();
      const assignee = assigneeByBooth.get(key) ?? "Unassigned";
      return {
        name,
        assignee,
        reserved: assigneeByBooth.has(key),
      };
    });
  }, [allBoothNames, assigneeByBooth]);

  const reservedCount = assignments.length;
  const totalBooths = allBoothNames.length;

  const lastUpdatedLabel = lastRefreshedUtc
    ? new Date(lastRefreshedUtc).toLocaleString()
    : null;

  const handleBoothClick = (boothName: string) => {
    setModalBooth(boothName);
  };

  const closeModal = () => setModalBooth(null);

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        Click a booth on the map to view its assignment and nearby booths.
      </p>

      <div className="relative inline-block max-w-full border rounded overflow-hidden bg-white">
        <img
          src={floorplanUrl}
          alt="Industry Showcase floorplan"
          className="block max-w-full h-auto"
        />
        {booths.map((booth, idx) => {
          const region = parseBoothRegion(booth.regionJson ?? undefined);
          if (!region || !booth.name) return null;
          const [left, top, width, height] = region.coords;
          const key = booth.name.trim().toLowerCase();
          const reserved = reservedSet.has(key);
          const isModalTarget =
            modalBooth?.trim().toLowerCase() === key;

          let className =
            "absolute box-border border-2 transition-colors flex items-center justify-center text-xs font-semibold cursor-pointer ";
          if (reserved) {
            className +=
              "border-gray-500 bg-gray-500/50 hover:bg-gray-500/65 text-white ";
          } else {
            className +=
              "border-green-600 bg-green-500/30 hover:bg-green-500/50 text-gray-900 ";
          }
          if (isModalTarget) {
            className += "ring-2 ring-blue-500 ring-offset-1 ";
          }

          return (
            <button
              key={`${booth.name}-${idx}`}
              type="button"
              title={`Booth ${booth.name} — ${getAssignee(booth.name)}`}
              className={className}
              style={{
                left: `${left}%`,
                top: `${top}%`,
                width: `${width}%`,
                height: `${height}%`,
              }}
              onClick={() => handleBoothClick(booth.name)}
            >
              {booth.name}
            </button>
          );
        })}
      </div>

      <BoothDetailModal
        show={modalBooth !== null}
        boothName={modalBooth}
        allBoothNames={allBoothNames}
        getAssignee={getAssignee}
        onClose={closeModal}
      />

      <div className="text-sm">
        <div className="flex flex-wrap gap-4 mb-2 text-xs text-gray-600">
          <span className="inline-flex items-center gap-1">
            <span className="w-4 h-4 border-2 border-green-600 bg-green-500/30" />
            Available
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="w-4 h-4 border-2 border-gray-500 bg-gray-500/50" />
            Reserved
          </span>
          {loading && <span>Updating assignments…</span>}
        </div>

        <details
          className="border border-gray-300 rounded-lg bg-white shadow-sm group"
          open={defaultPanelOpen}
        >
          <summary className="cursor-pointer list-none px-4 py-3 font-bold text-gray-900 hover:bg-gray-50 rounded-lg [&::-webkit-details-marker]:hidden">
            <span className="flex items-center justify-between gap-2">
              <span>Selected Booths</span>
              <span className="text-sm font-semibold text-gray-600">
                {reservedCount} reserved / {totalBooths} total
                <span className="ml-2 text-gray-400 group-open:rotate-180 inline-block transition-transform">
                  ▼
                </span>
              </span>
            </span>
          </summary>
          <div className="border-t border-gray-200 max-h-80 overflow-y-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-gray-100 sticky top-0">
                <tr>
                  <th className="p-2 border-b font-bold">Booth</th>
                  <th className="p-2 border-b font-bold">Assigned to</th>
                </tr>
              </thead>
              <tbody>
                {sortedBoothRoster.map((row) => (
                  <tr
                    key={row.name}
                    className={`${row.reserved ? "bg-gray-50" : ""} cursor-pointer hover:bg-blue-50`}
                    onClick={() => handleBoothClick(row.name)}
                  >
                    <td className="p-2 border-b font-semibold">{row.name}</td>
                    <td
                      className={`p-2 border-b ${
                        row.assignee === "Unassigned"
                          ? "text-gray-500 italic"
                          : "font-medium text-gray-900"
                      }`}
                    >
                      {row.assignee}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {totalBooths === 0 && (
              <p className="p-4 text-gray-500 text-sm">
                No booths configured on this form.
              </p>
            )}
          </div>
        </details>

        {lastUpdatedLabel && (
          <p className="mt-2 text-xs text-gray-500">
            Last updated {lastUpdatedLabel}
            {reservedCount > 0 &&
              ` · ${reservedCount} booth${reservedCount === 1 ? "" : "s"} reserved`}
          </p>
        )}
      </div>
    </div>
  );
};

export default BoothFloorplanViewer;
